<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue';
import { IonGrid } from '@ionic/vue';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';

import StoriesHeader from '@/components/stories/StoriesHeader.vue';
import StoriesSlider from '@/components/stories/StoriesSlider.vue';
import StorySlide from '@/components/stories/StorySlide.vue';
import { useStoriesPauseResume } from '@/composables/stories/useStoriesPauseResume';
import { useStoriesProgressBar } from '@/composables/stories/useStoriesProgressBar';
import { useStoriesSwiper } from '@/composables/stories/useStoriesSwiper';
import { useStoriesStore } from '@/stores/stories';
import type { Story } from '@/types/stories';

const storiesStore = useStoriesStore();

const story = computed<Story | null>(() => {
  if (storiesStore.stories.length) {
    return storiesStore.stories[0];
  }
  return null;
});

const showOverlay = computed(
  () =>
    storiesStore.overlayOpen &&
    storiesStore.loaded &&
    storiesStore.stories.length,
);

// Progress bar composable
const {
  progressBars,
  setProgressBarRef,
  resetAllProgressBars,
  getProgressBarWidth,
  setProgressBarWidth,
  startProgressBarAnimation,
} = useStoriesProgressBar();

// Pause/resume composable
const { pauseProgressBar, resumeProgressBar, resetPauseState, setSlideState } =
  useStoriesPauseResume(progressBars, getProgressBarWidth, setProgressBarWidth);

// Swiper composable
const {
  swiperRef,
  initializeSwiper,
  cleanup,
  cleanupSlideTimeout,
  setCurrentSlideTimeout,
  scheduleSlideTransition,
} = useStoriesSwiper(
  story,
  progressBars,
  resetAllProgressBars,
  startProgressBarAnimation,
  setSlideState,
  resetPauseState,
);

const handlePauseStart = (
  slideIndex: number,
  _event: MouseEvent | TouchEvent,
) => {
  if (!swiperRef.value || swiperRef.value.realIndex !== slideIndex) {
    return;
  }

  pauseProgressBar(slideIndex, swiperRef.value, cleanupSlideTimeout);
};

const handlePauseEnd = (slideIndex: number, event: MouseEvent | TouchEvent) => {
  if (!swiperRef.value || swiperRef.value.realIndex !== slideIndex) {
    return;
  }

  // Prevent Swiper from handling this event
  event.preventDefault();
  event.stopPropagation();

  const timeoutId = resumeProgressBar(
    slideIndex,
    swiperRef.value,
    scheduleSlideTransition,
  );
  if (timeoutId !== null) {
    setCurrentSlideTimeout(timeoutId);
  }
};

watch(
  () => storiesStore.overlayOpen,
  (isOpen) => {
    if (!isOpen) {
      cleanup();
    }
  },
);

onBeforeUnmount(() => {
  cleanup();
});
</script>

<template>
  <div
    v-if="showOverlay"
    class="stories-overlay-container"
  >
    <ion-grid class="stories-grid">
      <stories-header />
      <stories-slider
        :story="story"
        :swiper-ref="swiperRef"
        @swiper="initializeSwiper"
      >
        <template #slide="{ item, index }">
          <story-slide
            :item="item"
            :index="index"
            :is-active="swiperRef?.realIndex === index"
            :progress-bar-ref="setProgressBarRef(index)"
            @pause-start="handlePauseStart(index, $event)"
            @pause-end="handlePauseEnd(index, $event)"
          />
        </template>
      </stories-slider>
    </ion-grid>
  </div>
</template>

<style lang="less" scoped>
.stories-overlay-container {
  position: fixed;
  top: calc(calc(var(--base-gap) / 2) + env(safe-area-inset-top));
  left: calc(var(--gap-xs) / 2);
  width: calc(100dvw - calc(var(--base-gap) / 2));
  height: calc(
    100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) -
      var(--base-gap)
  );
  background-color: #fff;
  z-index: 1000;
  border-radius: var(--base-border-radius);
  color: var(--primary-900);

  .stories-grid {
    margin: 0;
    padding: 0;
    overflow: hidden;
    height: 100%;
  }
}
</style>
